"use client";

/**
 * PlaceTradeContainer — source-aware production wiring for PlaceTrade V2.
 *
 * Replicates the legacy TradingPanel execution surface across the three
 * venues the app supports today:
 *
 *   polymarket  → Safe wallet, Polygon/USDC.e, endpoints /api/trade/*
 *                 Supports market buy/sell + limit buy/sell (price + expiry + postOnly).
 *                 Uses live prices from MarketPriceProvider if one is mounted.
 *   kalshi      → Solana wallet, USDC on Solana, /api/dflow/execute (DFlow sponsor).
 *                 Market buy/sell only — DFlow has no exposed limit layer.
 *   predict     → EOA on BSC, USDT, /api/trading/predict/execute.
 *                 Market buy/sell only.
 *
 * All three share the same UI — the container just picks the right
 * balance source, currency, address, execute endpoint, and toast `source`
 * tag. Limit mode is hidden on non-polymarket sources via `supportsLimit`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useT } from "@/hooks/useT";

import PlaceTrade from "./index";
import type { Order, TxResult, TradeEvent, PlaceTradeProps, TradeSource } from "./types";
import { usePlaceTradeEnrichment } from "./hooks/usePlaceTradeEnrichment";
import { useOwnedPositions } from "./hooks/useOwnedPositions";

import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/useAuth";
import { useUserMe } from "@/contexts/UserMeContext";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useSolanaUSDCBalance } from "@/hooks/useSolanaUSDCBalance";
import { useEnableTrading } from "@/hooks/useEnableTrading";
import { useHyperliquidOrderbook } from "@/hooks/useHyperliquidOrderbook";
import { useDepositModalStore } from "@/stores/useDepositModalStore";
import { useBscDepositModalStore } from "@/stores/useBscDepositModalStore";
import { useMarketPriceOptional } from "../../context/MarketPriceContext";
import TradeToast from "@/app/components/TradeToast";
import { useReferralAttribution, referralBody } from "@/hooks/useReferralAttribution";
import { notifyPortfolioRefresh } from "@/lib/portfolio-refresh";

interface ContainerProps {
  marketData: {
    slug: string;
    question: string;
    outcomePrices: number[];
    outcomes: string[];
    clobTokenIds: string[];
    image?: string | null;
    endDate?: string | null;
    decimalPrecision?: number;
    groupItemTitle?: string | null;
  };
  isActive: boolean;
  source: TradeSource;
  conditionId?: string;
  kalshiTicker?: string;
  dflowMints?: { yesMint?: string; noMint?: string } | null;
  predictMarketId?: string;
  initialOutcomeIndex?: 0 | 1 | null;
  /** Pre-select the buy/sell side. Used when the modal opens from a
   *  /portfolio "Sell" CTA so the panel lands on the SELL tab. */
  initialSide?: "buy" | "sell";
  onOutcomeChange?: (idx: 0 | 1) => void;
  contextHeader?: React.ReactNode;
  compact?: boolean;
  /** Post-author referral attribution (optional). When the caller already
   *  has postId/authorId (e.g. ActivityFeed opening the modal for a user
   *  post), pass them here. Otherwise the hook reads URL params. */
  referralPostId?: string | null;
  referralAuthorId?: string | null;
  /** Pre-known owned position used to seed `useOwnedPositions` so the
   *  "You own X @ Y" line in the SellContext shows up synchronously
   *  with the modal opening. Caller (TradingModal opened from a
   *  portfolio Sell click) already has the data; without this seed
   *  the line stays blank until /api/dflow/positions or
   *  /api/portfolio/positions returns. */
  seedOwnedPosition?: { outcome: "yes" | "no"; shares: number; avgPrice: number; currentPrice?: number; displayLabel?: string } | null;
}

export default function PlaceTradeContainer(props: ContainerProps) {
  const {
    marketData,
    isActive,
    source,
    conditionId,
    kalshiTicker,
    dflowMints,
    predictMarketId,
    initialOutcomeIndex,
    initialSide,
    onOutcomeChange,
    contextHeader,
    compact,
    referralPostId,
    referralAuthorId,
    seedOwnedPosition,
  } = props;

  const t = useT();
  const { isLoggedIn, login } = useAuth();
  const { user: userMe } = useUserMe();
  const queryClient = useQueryClient();

  // Post-author referral attribution: prop override → URL params → none.
  // Fingerprint is always computed client-side for anti-abuse comparison.
  const referral = useReferralAttribution({
    postId: referralPostId,
    authorId: referralAuthorId,
  });

  // ---------- Source-specific balance + address ----------

  const pmCtx = usePolymarketBalanceContext();
  const sol = useSolanaUSDCBalance();

  const [predictBalance, setPredictBalance] = useState<number | null>(null);
  const [predictAddress, setPredictAddress] = useState<string | null>(null);
  useEffect(() => {
    if (source !== "predict" || !isLoggedIn) return;
    let mounted = true;
    const fetchBalance = async () => {
      try {
        const r = await authFetch("/api/trading/predict/balance");
        if (!r.ok || !mounted) return;
        const data = await r.json();
        setPredictBalance(Number(data.balance?.usdt ?? data.usdt ?? 0));
        setPredictAddress(data.address ?? data.walletAddress ?? null);
      } catch {
        // Silent — we degrade to 0 balance.
      }
    };
    fetchBalance();
    const id = setInterval(fetchBalance, 60000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [source, isLoggedIn]);

  // Hyperliquid balance (HyperCore margin account, USDC).
  const [hlBalance, setHlBalance] = useState<number | null>(null);
  const [hlAddress, setHlAddress] = useState<string | null>(null);
  useEffect(() => {
    if (source !== "hyperliquid" || !isLoggedIn) return;
    let mounted = true;
    const fetchBalance = async () => {
      try {
        const r = await authFetch("/api/trading/hyperliquid/balance");
        if (!r.ok || !mounted) return;
        const data = await r.json();
        // HIP-4 outcomes consume the spot stable ledger. The matching
        // engine debits USDH (HL's HIP-4 quote token); USDC gets
        // auto-wrapped via the @230 pair when the user is short. So
        // the tradable balance is the full spot stables sum
        // (USDC + USDH + USDE + USDT0). Falls back to a manual
        // sum when the backend predates spotStablesUsd.
        const spotUsd = Number(data?.balance?.spotUsd ?? 0);
        const spotUsdh = Number(data?.balance?.spotUsdh ?? 0);
        const spotStables = Number(data?.balance?.spotStablesUsd ?? spotUsd + spotUsdh);
        const perp = Number(data?.balance?.withdrawableUsd ?? data?.balance?.perpUsd ?? 0);
        const totalCombined = Number(data?.balance?.hyperCoreUsd ?? spotStables + perp);
        // Prefer the spot stables for trade-size validation; fall back
        // to combined total when the user only has perp (rare — it
        // means they bridged but never moved spot→perp).
        setHlBalance(spotStables > 0 ? spotStables : totalCombined);
        setHlAddress(data?.balance?.walletAddress ?? null);
      } catch {
        // Silent.
      }
    };
    fetchBalance();
    const id = setInterval(fetchBalance, 60000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [source, isLoggedIn]);

  const balanceAmount =
    source === "polymarket" ? pmCtx.balance ?? 0
    : source === "kalshi" ? sol.balance ?? 0
    : source === "hyperliquid" ? hlBalance ?? 0
    : predictBalance ?? 0;

  const balanceAddress =
    source === "polymarket" ? pmCtx.walletAddress ?? "0x0000000000000000000000000000000000000000"
    : source === "kalshi" ? sol.solanaAddress ?? ""
    : source === "hyperliquid" ? hlAddress ?? "0x0000000000000000000000000000000000000000"
    : predictAddress ?? "0x0000000000000000000000000000000000000000";

  // Polymarket V2 settles in pUSD (USDC.e auto-wraps pre-trade). The
  // balance number already combines USDC.e + pUSD under the hood, so the
  // label should reflect the on-venue collateral the user actually spends.
  // Hyperliquid uses HyperCore USDC as collateral.
  const balanceCurrency: "pUSD" | "USDC" | "USDT" | "HL_USDC" =
    source === "polymarket" ? "pUSD"
    : source === "kalshi" ? "USDC"
    : source === "hyperliquid" ? "HL_USDC"
    : "USDT";

  // ---------- Deposit / funding modals ----------

  const openPmDeposit = useDepositModalStore((s) => s.open);
  const openBscDeposit = useBscDepositModalStore((s) => s.open);
  const onAddFunds =
    source === "polymarket" ? () => openPmDeposit("card")
    : source === "predict" ? () => openBscDeposit()
    : source === "hyperliquid" ? () => {
      // Dispatched event picked up by the global Hyperliquid deposit modal.
      try { window.dispatchEvent(new CustomEvent("frenflow:hl-deposit-open")); } catch { /* noop */ }
    }
    : () => openPmDeposit("card"); // kalshi: reuse card deposit; Solana funding UX TBD

  const { enableTrading } = useEnableTrading();

  // ---------- Live prices ----------
  // When a MarketPriceProvider wraps the panel (Polymarket market/event pages),
  // sip real-time WebSocket prices off it. Otherwise fall back to the static
  // `marketData.outcomePrices` prop (parent already recomputes those on each
  // render for Polymarket, so this is still fresh — just less often).
  const livePriceCtx = useMarketPriceOptional();
  const yesPrice =
    livePriceCtx?.buyPrices.buyYes ?? marketData.outcomePrices?.[0] ?? 0.5;
  const noPrice =
    livePriceCtx?.buyPrices.buyNo ?? marketData.outcomePrices?.[1] ?? 0.5;

  // ---------- Hyperliquid bid/ask per outcome ----------
  // HL exposes one orderbook per outcome side asset id (YES + NO are
  // independent books). Derive both asset ids from the slug suffix
  // (matching backend's outcomeAssetId encoding) and subscribe to both
  // so the SELL preview can show bestBid (what we'll receive) and BUY
  // preview shows bestAsk (what we'll pay). Without this, mid pricing
  // (livePriceCtx.buyPrices) lies on wide-spread HL markets and the
  // backend's L2-book min-notional gate rejects after the user clicks.
  const hlAssetIds = useMemo(() => {
    if (source !== "hyperliquid") return { yes: null as number | null, no: null as number | null };
    const slug = marketData.slug || "";
    const m = slug.match(/-q-?\d+-o(\d+)-(yes|no)$/);
    if (!m) return { yes: null, no: null };
    const outcomeId = parseInt(m[1], 10);
    if (!Number.isFinite(outcomeId)) return { yes: null, no: null };
    // outcomeAssetId encoding from backend/services/hyperliquid/client.ts.
    return {
      yes: 100_000_000 + 10 * outcomeId + 0,
      no: 100_000_000 + 10 * outcomeId + 1,
    };
  }, [source, marketData.slug]);
  const hlEnabled = source === "hyperliquid";
  const { book: yesBook } = useHyperliquidOrderbook({
    assetId: hlAssetIds.yes,
    enabled: hlEnabled && !!hlAssetIds.yes,
    depth: 2,
  });
  const { book: noBook } = useHyperliquidOrderbook({
    assetId: hlAssetIds.no,
    enabled: hlEnabled && !!hlAssetIds.no,
    depth: 2,
  });
  const hlBidAsk = useMemo(() => {
    if (source !== "hyperliquid") return null;
    const fromBook = (b: typeof yesBook) => {
      if (!b) return null;
      const bestBid = b.bids[0]?.price;
      const bestAsk = b.asks[0]?.price;
      if (!Number.isFinite(bestBid) || !Number.isFinite(bestAsk)) return null;
      return { bestBid: bestBid as number, bestAsk: bestAsk as number };
    };
    return { yes: fromBook(yesBook), no: fromBook(noBook) };
  }, [source, yesBook, noBook]);

  // ---------- Polymarket bid/ask per outcome ----------
  // `yesPrice`/`noPrice` are the real, sanity-checked ASKS of each outcome
  // token (bestAsk from getBuyPrices, via the live WS price context). In a
  // binary CLOB the two outcome books are exactly complementary, so the best
  // BID of one side = 1 − best ASK of the other. Verified empirically against
  // clob.polymarket.com/book (diff 0.0000) and the py-clob-client semantics.
  // Surfacing this makes the SELL preview show the real executable BID instead
  // of the ASK/mid.
  // Bug fix (2026-06-09): on a wide-spread weather market the panel showed the
  // ask (e.g. 94¢) while the sell actually filled at the 49¢ bid (user
  // am8519672). Now SELL previews and sell-side math use the bid from the start
  // — the walked-orderbook hook still refines it once the book poll lands.
  const pmBidAsk = useMemo(() => {
    if (source !== "polymarket") return null;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const ya = yesPrice;
    const na = noPrice;
    if (!(ya > 0 && ya < 1 && na > 0 && na < 1)) return null;
    return {
      yes: { bestAsk: ya, bestBid: clamp(1 - na) },
      no: { bestAsk: na, bestBid: clamp(1 - ya) },
    };
  }, [source, yesPrice, noPrice]);

  // ---------- Enrichment (smart money + live trades) ----------
  const { smartMoney, liveTrades } = usePlaceTradeEnrichment(
    source === "polymarket" ? conditionId : undefined,
  );
  // Owned-positions lookup needs source-aware wiring — /api/portfolio/positions
  // is Polymarket only. For Kalshi, the hook queries /api/dflow/positions
  // by ticker; marketData.slug is already the Kalshi ticker on that path.
  const ownedPositions = useOwnedPositions(
    source === "predict" ? undefined : marketData.slug,
    isLoggedIn,
    source,
    seedOwnedPosition,
    source === "polymarket" ? conditionId : undefined,
  );

  // ---------- Telemetry ----------
  // No-op until /api/telemetry/trade is implemented on the backend (the
  // PlaceTrade v2 proposal documents it but the endpoint was never built).
  // Every preset click / slider drag was firing a 404 in the browser
  // console — silenced here. Restore the fetch + body when the sink and
  // telemetry_events table are ready.
  const telemetry = useCallback((_e: TradeEvent) => {
    /* intentional no-op */
  }, []);

  // ---------- Submit router (per source) ----------
  const handleSubmit = useCallback(async (order: Order): Promise<TxResult> => {
    if (!isLoggedIn) {
      login();
      return { ok: false, error: "Login required" };
    }

    const selectedIdx: 0 | 1 = order.outcome === "yes" ? 0 : 1;
    onOutcomeChange?.(selectedIdx);
    // Real outcome label from the market API ("Over", "Up", "Lakers"). The
    // container only ever runs inside a flow that already has marketData
    // populated, so the empty-string fallback should never surface — if it
    // does, downstream telemetry / API calls treat it as a missing field
    // instead of silently shipping a wrong "Yes"/"No" label.
    const selectedOutcomeValue = marketData.outcomes?.[selectedIdx] ?? "";
    const tokenId = marketData.clobTokenIds?.[selectedIdx];
    const currentPrice = selectedIdx === 0 ? yesPrice : noPrice;
    const isLimit = order.mode === "limit";
    // Base price for MARKET orders = the live top-of-book we'll actually cross
    // (best ask for BUY / best bid for SELL, from the panel's walked book),
    // NOT the displayed mid/last. The backend builds the slippage ceiling on
    // top of this, so on a wide-spread penny market the ceiling lands above the
    // ask instead of below it (which silently 0-filled the FOK). Falls back to
    // the displayed price until the book has loaded.
    const marketBasePrice =
      typeof order.execBasePrice === "number" && order.execBasePrice > 0
        ? order.execBasePrice
        : currentPrice;
    const limitPriceDecimal =
      isLimit && order.limitPriceCents != null
        ? order.limitPriceCents / 100
        : marketBasePrice;

    // Balance / shares check.
    //   BUY (USD-mode):    order.amount is USD → check against balanceAmount
    //   BUY (SHARES-mode): order.amount is SHARES → cost = shares × price
    //   SELL:              order.amount is SHARES → check against ownedPositions
    if (order.side === "buy") {
      const buyCostUsd = source === "hyperliquid"
        ? order.amount * Math.max(currentPrice, 0.001)
        : order.amount;
      if (balanceAmount < buyCostUsd) {
        return {
          ok: false,
          error: `Insufficient balance. Need $${buyCostUsd.toFixed(2)}, have $${balanceAmount.toFixed(2)}.`,
        };
      }
    }
    if (order.side === "sell") {
      const owned = ownedPositions?.[order.outcome]?.shares ?? 0;
      if (order.amount > owned + 0.001) {
        return {
          ok: false,
          error: `Exceeds your position. You own ${owned.toFixed(2)} shares.`,
        };
      }
    }

    // --------- POLYMARKET ---------
    if (source === "polymarket") {
      if (!pmCtx.tradingEnabled) {
        const r = await enableTrading();
        if (!r.success) return { ok: false, error: r.error || "Enable trading failed" };
      }
      if (isLimit && (order.limitPriceCents == null || order.limitPriceCents <= 0)) {
        return { ok: false, error: "Invalid limit price" };
      }
      try {
        let response: Response;
        const refBody = referralBody(referral);
        if (order.side === "sell") {
          // order.amount is already in SHARES (Polymarket-native SELL UX).
          response = await authFetch("/api/trade/sell", {
            method: "POST",
            body: JSON.stringify({
              marketSlug: marketData.slug,
              outcome: selectedOutcomeValue,
              shares: order.amount,
              price: limitPriceDecimal,
              orderType: isLimit ? "limit" : "market",
              ...(isLimit && order.expiresIn ? { expiresIn: order.expiresIn } : {}),
              ...(isLimit && order.postOnly ? { postOnly: true } : {}),
              ...(tokenId ? { tokenId } : {}),
              ...refBody,
            }),
          });
        } else if (isLimit) {
          response = await authFetch("/api/trade/limit-order", {
            method: "POST",
            body: JSON.stringify({
              marketSlug: marketData.slug,
              outcome: selectedOutcomeValue,
              side: "BUY",
              price: limitPriceDecimal,
              amount: order.amount,
              ...(order.expiresIn ? { expiresIn: order.expiresIn } : {}),
              ...(order.postOnly ? { postOnly: true } : {}),
              ...(tokenId ? { tokenId } : {}),
              ...refBody,
            }),
          });
        } else {
          response = await authFetch("/api/trade/execute", {
            method: "POST",
            body: JSON.stringify({
              marketSlug: marketData.slug,
              outcome: selectedOutcomeValue,
              amount: order.amount,
              price: marketBasePrice,
              side: "BUY",
              ...(tokenId ? { tokenId } : {}),
              // 2026-05-26: Auto-slippage override. PlaceTrade computes
              // `order.slippagePct` from the live orderbook only when
              // `userMe.slippage_mode === 'auto'`; otherwise it's
              // undefined and the backend keeps using the user's saved
              // default_slippage_pct.
              ...(typeof order.slippagePct === "number" ? { slippage: order.slippagePct } : {}),
              ...refBody,
            }),
          });
        }
        const result = await response.json();
        if (!response.ok || !result.success) {
          return { ok: false, error: result.error || "Trade failed" };
        }
        notifyPortfolioRefresh();
        queryClient.invalidateQueries({ queryKey: ["portfolio"] }).catch(() => {});
        // Polymarket /api/trade/execute returns `executedPrice` /
        // `executedSize` (the real fill avg / size after orderbook walk).
        // The previous `result.avgPrice` / `result.shares` lookups never
        // matched — those keys don't exist on the response — so the
        // toast always fell back to `currentPrice` and `amount/price`,
        // which is the live bestAsk at render time. On fast markets
        // (crypto 5m up/down) that's several cents off the real fill.
        const execPrice = Number(result.executedPrice ?? result.avgPrice);
        const execSize = Number(result.executedSize ?? result.shares);
        const shares = Number.isFinite(execSize) && execSize > 0
          ? execSize
          : order.side === "sell"
            ? order.amount
            : order.amount / Math.max(currentPrice, 0.0001);
        const avgPrice = Number.isFinite(execPrice) && execPrice > 0
          ? execPrice
          : currentPrice;
        const notionalUsd = order.side === "sell" ? shares * avgPrice : order.amount;
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: order.side.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeValue,
            shares, price: avgPrice, notionalValue: notionalUsd,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "polymarket",
          }} />
        ), { duration: 6000, position: "bottom-right" });
        return { ok: true, shares, avgPrice, txHash: result.txHash };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Trade failed" };
      }
    }

    // --------- KALSHI (DFlow) ---------
    if (source === "kalshi") {
      if (isLimit) return { ok: false, error: "Limit orders not available on Kalshi" };
      try {
        const response = await authFetch("/api/dflow/execute", {
          method: "POST",
          body: JSON.stringify({
            kalshiTicker: kalshiTicker || marketData.slug,
            marketTitle: marketData.question,
            action: order.side,
            outcome: order.outcome,
            amountUsd: order.side === "buy" ? order.amount : undefined,
            shares: order.side === "sell" ? order.amount : undefined,
            yesMint: dflowMints?.yesMint,
            noMint: dflowMints?.noMint,
            ...referralBody(referral),
          }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          return { ok: false, error: result.error || "Trade failed" };
        }
        notifyPortfolioRefresh();
        window.dispatchEvent(new Event("dflow:refresh"));
        queryClient.invalidateQueries({ queryKey: ["dflow", "positions"] }).catch(() => {});
        const shares = typeof result.shares === "number"
          ? result.shares
          : order.side === "sell" ? order.amount : order.amount / Math.max(currentPrice, 0.0001);
        const avgPrice = typeof result.avgPrice === "number" ? result.avgPrice : currentPrice;
        const notionalUsd = order.side === "sell" ? shares * avgPrice : order.amount;
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: order.side.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeValue,
            shares, price: avgPrice, notionalValue: notionalUsd,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "kalshi",
          }} />
        ), { duration: 6000, position: "bottom-right" });
        return { ok: true, shares, avgPrice };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Trade failed" };
      }
    }

    // --------- HYPERLIQUID ---------
    if (source === "hyperliquid") {
      // Hyperliquid pairs (question, outcome, side). The marketData.slug is
      // the side-specific slug; we extract the outcomeIndex from the URL by
      // calling the public market detail endpoint, but for the modal flow
      // the caller already passes hlOutcomeIndex via the marketData seed.
      // Fall back to outcome 0 if missing.
      let outcomeIndex = (marketData as any).hlOutcomeIndex;
      if (outcomeIndex == null) {
        try {
          const detail = await fetch(`/api/markets/hyperliquid/${encodeURIComponent(marketData.slug)}`).then((r) => r.json());
          outcomeIndex = detail?.market?.outcome_id ?? 0;
        } catch {
          outcomeIndex = 0;
        }
      }
      try {
        const isLimitHl = order.mode === "limit";
        // HL container forces inputUnit="SHARES" → order.amount is already
        // integer shares for both BUY and SELL. No USD/price conversion.
        const sizeShares = order.amount;
        // Slippage tolerance — per-source override first, global default
        // second, 5% floor third. Same fallback chain the HL adapter
        // uses; PlaceTradeContainer is the alternative entry point so
        // we mirror it instead of hardcoding 2%.
        const slippageBps = (() => {
          try {
            const me = (typeof window !== "undefined" ? (window as any).__userMeCache : null) || {};
            const perSource = Number(me?.slippage_per_source?.hyperliquid);
            if (Number.isFinite(perSource) && perSource > 0) return Math.min(100, Math.max(0.1, perSource));
            const global = Number(me?.default_slippage_pct);
            if (Number.isFinite(global) && global > 0) return Math.min(100, Math.max(0.1, global));
          } catch {}
          return 5;
        })();
        const slippageMul = order.side === "buy" ? (1 + slippageBps / 100) : (1 - slippageBps / 100);
        const limitPriceForHl = isLimitHl ? limitPriceDecimal : currentPrice * slippageMul;
        const response = await authFetch("/api/trading/hyperliquid/execute", {
          method: "POST",
          body: JSON.stringify({
            outcomeIndex,
            side: selectedIdx === 0 ? "YES" : "NO",
            tradeSide: order.side.toUpperCase(),
            size: sizeShares,
            limitPrice: Math.max(0.001, Math.min(0.999, limitPriceForHl)),
            tif: isLimitHl ? (order.postOnly ? "Alo" : "Gtc") : "Ioc",
            // No reduceOnly for HL — outcomes are spot-like (shares in
            // spotClearinghouseState, not a perp position with a size
            // attribute). `r: true` is a perp-only concept and HL
            // either ignores it or rejects the order.
            surface: "web",
            ...referralBody(referral),
          }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          // If Hyperliquid trading isn't enabled / approved yet, surface the
          // global enable modal so the user can complete onboarding without
          // bouncing to a separate page.
          const msg = String(result.error || "");
          if (/not enabled|approval|onboarding|missing/i.test(msg)) {
            try { window.dispatchEvent(new CustomEvent("frenflow:hl-enable-open")); } catch { /* noop */ }
          }
          return { ok: false, error: result.error || "Trade failed" };
        }
        notifyPortfolioRefresh();
        const shares = sizeShares;
        const avgPrice = result.filled?.avgPx
          ? Number(result.filled.avgPx)
          : limitPriceForHl;
        const notionalUsd = shares * avgPrice;
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: order.side.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeValue,
            shares, price: avgPrice, notionalValue: notionalUsd,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "hyperliquid" as any,
          }} />
        ), { duration: 6000, position: "bottom-right" });
        return { ok: true, shares, avgPrice };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Trade failed" };
      }
    }

    // --------- PREDICT ---------
    if (source === "predict") {
      if (isLimit) return { ok: false, error: "Limit orders not available on Predict" };
      try {
        const response = await authFetch("/api/trading/predict/execute", {
          method: "POST",
          body: JSON.stringify({
            marketId: predictMarketId || marketData.slug,
            outcome: selectedOutcomeValue,
            outcomeIndex: selectedIdx,
            amount: order.amount,
            price: currentPrice,
            side: order.side.toUpperCase(),
            ...referralBody(referral),
          }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          return { ok: false, error: result.error || "Trade failed" };
        }
        notifyPortfolioRefresh();
        const shares = typeof result.shares === "number" ? result.shares : order.amount / Math.max(currentPrice, 0.0001);
        const avgPrice = typeof result.avgPrice === "number" ? result.avgPrice : currentPrice;
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: order.side.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeValue,
            shares, price: avgPrice, notionalValue: order.amount,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "predict",
          }} />
        ), { duration: 6000, position: "bottom-right" });
        return { ok: true, shares, avgPrice };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Trade failed" };
      }
    }

    return { ok: false, error: `Unsupported source: ${source}` };
  }, [
    isLoggedIn, login, source, marketData, yesPrice, noPrice, balanceAmount,
    pmCtx.tradingEnabled, enableTrading, queryClient,
    kalshiTicker, dflowMints, predictMarketId,
    ownedPositions, onOutcomeChange,
  ]);

  // ---------- Sell-seed key ----------
  // Mint a fresh string whenever the parent passes a brand-new seed object
  // (which it does on every MyPositions Sell click — `setPanelSellSeed({...})`).
  // PlaceTrade reads this through `defaults.seedKey` to know it should drop
  // the post-trade success card and show the SELL form again.
  const sellSeedKey = useMemo(
    () => (seedOwnedPosition ? String(Date.now()) : undefined),
    [seedOwnedPosition],
  );

  // ---------- Assemble PlaceTrade V2 props ----------

  const placeTradeProps = useMemo<PlaceTradeProps>(() => ({
    market: {
      id: marketData.slug,
      conditionId,
      slug: marketData.slug,
      question: marketData.question,
      outcomes: { yes: yesPrice, no: noPrice },
      // outcomeLabels MUST be the real pair from the API (Over/Under,
      // Up/Down, team names). Fall back to empty strings rather than a
      // wrong "Yes"/"No" — the parent flow guarantees the array is
      // populated; an empty label surfaces a real bug instead of masking it.
      outcomeLabels: [
        marketData.outcomes?.[0] ?? "",
        marketData.outcomes?.[1] ?? "",
      ] as [string, string],
      source,
      endDate: marketData.endDate ?? null,
      decimalPrecision: marketData.decimalPrecision,
      supportsLimit: source === "polymarket" || source === "hyperliquid",
      image: marketData.image ?? null,
      // Hyperliquid HIP-4 outcomes have szDecimals=0 → integer shares only.
      // Switch the input semantic so the user picks share count, not USD.
      inputUnit: source === "hyperliquid" ? ("SHARES" as const) : ("USD" as const),
      // HL enforces a $10 min notional per order.
      minOrderUsd: source === "hyperliquid" ? 10 : undefined,
      // Per-outcome bid/ask — drives side-aware preview pricing in PlaceTrade
      // so SELL shows bestBid (what you receive) and BUY shows bestAsk (what
      // you pay). HL reads its native L2 books; Polymarket derives it from the
      // complementary ASKS (bestBid = 1 − ask of the other outcome). Kalshi /
      // Predict still fall back to mid-price preview (DFlow/Predict return the
      // executable price server-side).
      bidAsk: source === "hyperliquid" ? hlBidAsk : pmBidAsk,
      // Surface CLOB tokens / predict id for the walked-orderbook hook —
      // PlaceTrade uses these to compute the real weighted fill price
      // for BUY/SELL instead of the mid/last-trade estimate that
      // misleads on wide-spread or thin books.
      clobTokenIds: source === "polymarket" ? marketData.clobTokenIds : undefined,
      predictMarketId: source === "predict" ? predictMarketId : undefined,
    },
    balance: {
      amount: balanceAmount,
      currency: balanceCurrency,
      address: balanceAddress,
    },
    user: {
      connected: isLoggedIn,
      totalTrades: (userMe?.stats?.total_trades as number | undefined) ?? 0,
      ninjaMode: (userMe as { ninja_mode_enabled?: boolean } | null)?.ninja_mode_enabled ?? true,
    },
    urgency: marketData.endDate ? { resolvesAt: marketData.endDate } : null,
    smartMoney,
    liveTrades,
    ownedPositions,
    defaults: {
      mode: "market",
      side: initialSide ?? "buy",
      outcome: initialOutcomeIndex === 1 ? "no" : "yes",
      amount: 0,
      // Bumped via the parent's seed object identity — `setPanelSellSeed({...})`
      // mints a new literal each click, so this changes even on a second
      // Sell click for the same position. PlaceTrade exits its post-trade
      // success card whenever this string flips.
      seedKey: sellSeedKey,
    },
    // Single accumulative preset row.
    //   - User has saved quick_buy_amounts → use those (+$5/+$25/+$100 + Max)
    //   - Otherwise default to +$1/+$10/+$50/Max
    // Two rows tested poorly with Nic — too many decisions, both saying
    // "$X" but with different semantics ("+$X" vs "$X replace").
    presets: (() => {
      // HL inputs are shares (integers), not USD. Use small integer presets
      // that read as +1/+5/+10 share increments instead of dollar amounts.
      if (source === "hyperliquid") {
        return [1, 5, 10, "max"] as Array<number | "max">;
      }
      const saved = (userMe as { quick_buy_amounts?: number[] } | null)?.quick_buy_amounts;
      const valid = Array.isArray(saved) ? saved.filter((n) => typeof n === "number" && n > 0) : [];
      const row: Array<number | "max"> = valid.length > 0 ? [...valid] : [1, 10, 50];
      row.push("max");
      return row;
    })(),
    customPresets: (() => {
      if (source === "hyperliquid") return false;
      const saved = (userMe as { quick_buy_amounts?: number[] } | null)?.quick_buy_amounts;
      const valid = Array.isArray(saved) ? saved.filter((n) => typeof n === "number" && n > 0) : [];
      return valid.length > 0;
    })(),
    onSubmit: handleSubmit,
    onConnectWallet: login,
    onAddFunds,
    telemetry,
    compact,
  }), [
    marketData, conditionId, source, yesPrice, noPrice, hlBidAsk, pmBidAsk,
    balanceAmount, balanceCurrency, balanceAddress,
    isLoggedIn, userMe, smartMoney, liveTrades, ownedPositions,
    initialOutcomeIndex, initialSide, sellSeedKey,
    handleSubmit, login, onAddFunds, telemetry, compact,
  ]);

  if (!isActive) {
    // Two-state resolved banner.
    //
    // 1) Winner known — prices have settled to 1.0 / 0.0 (oracle finished).
    //    Show the winning outcome BIG with its native side colour
    //    (green for UP/YES, red for DOWN/NO) so the banner reads as a
    //    "winner reveal", not a generic "trading disabled" notice.
    // 2) Awaiting resolution — market closed but oracle still verifying
    //    (typical 5m crypto up/down: ~30-60s gap between close and
    //    chainlink finality, longer for UMA-disputed markets). Show a
    //    pulsing "Awaiting resolution" placeholder in the same slot
    //    until step 1 fires, instead of the generic "No longer
    //    accepting trades" copy that gave no signal of progress.
    // Prefer live WS prices over the static gamma snapshot. Gamma's
    // `outcomePrices` only refreshes on a polled cycle (often a few
    // minutes lag), so a market that already resolved on PM stays at
    // [0.31, 0.69] in the snapshot for a while — the panel froze in
    // "Awaiting resolution" even after the oracle finalised. Live
    // prices from MarketPriceProvider flip to [1, 0] (or [0, 1]) the
    // instant the CLOB orderbook collapses, so we promote them when
    // available and fall back to the snapshot.
    const livePricesArr = livePriceCtx?.livePrices;
    const prices = (Array.isArray(livePricesArr) && livePricesArr.length === 2 && livePricesArr.some(p => typeof p === 'number'))
      ? livePricesArr
      : (marketData.outcomePrices || []);
    const outs = marketData.outcomes || [];
    let winner: { label: string; side: "up" | "down" } | null = null;
    if (prices.length === 2 && outs.length === 2) {
      // Multi-outcome event card (e.g. "Will X win the election?") uses
      // groupItemTitle as the subject and rewrites generic Yes/No to
      // "{subject}" / "Not {subject}". Non-Yes/No markets (Over/Under,
      // Up/Down, sports) use the API label as-is.
      const yesGeneric = String(outs[0] || '').toLowerCase() === 'yes';
      const noGeneric = String(outs[1] || '').toLowerCase() === 'no';
      const subject = marketData.groupItemTitle?.trim();
      if (prices[0] >= 0.99) {
        const apiLabel = String(outs[0] || '');
        const label = yesGeneric && subject ? subject : apiLabel;
        winner = { label, side: "up" };
      } else if (prices[1] >= 0.99) {
        const apiLabel = String(outs[1] || '');
        const label = noGeneric && subject ? `Not ${subject}` : apiLabel;
        winner = { label, side: "down" };
      }
    }

    if (winner) {
      const isUp = winner.side === "up";
      const winColor = isUp ? "#00FF9D" : "#FF1744";
      const winBg = isUp
        ? "bg-emerald-50 dark:bg-[#00FF9D]/[0.06] border-emerald-200 dark:border-[#00FF9D]/30"
        : "bg-red-50 dark:bg-[#FF1744]/[0.06] border-red-200 dark:border-[#FF1744]/30";
      return (
        <div className={`rounded-[5px] border p-5 ${winBg}`}>
          <div className="flex flex-col items-center gap-2 text-center">
            <span
              className="font-inter-tight text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: winColor }}
            >
              {t("placeTrade.resolvedWinner")}
            </span>
            <div className="flex items-center justify-center gap-2.5">
              <svg className="w-7 h-7" fill="none" stroke={winColor} strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span
                className="font-monument text-2xl md:text-3xl font-bold uppercase tracking-wide"
                style={{ color: winColor }}
              >
                {winner.label}
              </span>
            </div>
            <p className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400">
              {t("placeTrade.redeemPayout")}
            </p>
          </div>
        </div>
      );
    }

    // Awaiting resolution — closed, oracle still pending. Background
    // matches the hero / sidebar card surface (white / [#18181D]) so
    // the banner reads as "the trade panel rotated into a status
    // panel" instead of a different visual treatment.
    return (
      <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] p-5">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {t("placeTrade.awaitingResolution")}
          </span>
          <div className="flex items-center justify-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
            </span>
            <span className="font-monument text-base font-bold text-gray-900 dark:text-white">
              {t("placeTrade.verifyingOutcome")}
            </span>
          </div>
          <p className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400 max-w-[260px]">
            {t("placeTrade.verifyingOutcomeDesc")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {contextHeader}
      <PlaceTrade {...placeTradeProps} />
    </div>
  );
}
